import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import { resetParams } from 'dpl/actions/query_params';
import {
  FOLLOWABLE_ID,
  FOLLOWABLE_TYPE,
  REACTIONABLE_ID,
  REACTIONABLE_TYPE
} from 'dpl/constants/query_params';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_INTERACTION_TYPES,
  ANALYTICS_USER_JOURNEYS
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  createBreederProfileFollowMutationDefinition,
  createUserFollowMutationDefinition
} from 'dpl/favorites/utils/resourceDefinitions';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import {
  createUserReactionMutationDefinition,
  userReactionsQueryDefinition
} from 'dpl/breeder_posts/utils/resourceDefinitions';
import {
  REACTIONABLE_TYPES,
  REACTION_TYPES
} from 'dpl/breeder_posts/utils/constants';
import generateQueryKey from 'dpl/shared/fetching/utils/generateQueryKey';

const KNOWN_FOLLOWABLE_TYPES: string[] = Object.values(FOLLOWABLE_TYPES);
const KNOWN_REACTIONABLE_TYPES: string[] = Object.values(REACTIONABLE_TYPES);
export default function PostAuthActionProvider() {
  const dispatch = useDispatch();
  const { isLoggedIn, value: currentUserData } = useCurrentUser();
  const buyerProfileId = currentUserData?.data?.buyer_profile_id;

  const autoFollowType = useSelector(
    (state: { queryParams: Record<string, string> }) =>
      state.queryParams[FOLLOWABLE_TYPE]
  );
  const autoFollowId = useSelector(
    (state: { queryParams: Record<string, string> }) =>
      state.queryParams[FOLLOWABLE_ID]
  );

  const autoReactionableType = useSelector(
    (state: { queryParams: Record<string, string> }) =>
      state.queryParams[REACTIONABLE_TYPE]
  );
  const autoReactionableId = useSelector(
    (state: { queryParams: Record<string, string> }) =>
      state.queryParams[REACTIONABLE_ID]
  );

  const { mutateAsync: createBreederProfileFollow } = useResourceMutation(
    createBreederProfileFollowMutationDefinition(buyerProfileId)
  );

  const { mutateAsync: createUserFollow } = useResourceMutation(
    createUserFollowMutationDefinition()
  );

  const queryKey = generateQueryKey(userReactionsQueryDefinition());

  const { mutateAsync: createUserReaction } = useResourceMutation({
    ...createUserReactionMutationDefinition(),
    refetchAfter: 'success',
    queryKey
  });

  useEffect(() => {
    if (!autoFollowType || !autoFollowId) return;
    if (!isLoggedIn || !buyerProfileId) return;

    function clearParams() {
      dispatch(resetParams(FOLLOWABLE_TYPE, FOLLOWABLE_ID, { hash: true }));
    }

    if (!KNOWN_FOLLOWABLE_TYPES.includes(autoFollowType)) {
      clearParams();
      return;
    }

    async function run() {
      try {
        if (autoFollowType === FOLLOWABLE_TYPES.BREEDER_PROFILE) {
          await createBreederProfileFollow({
            body: { breeder_profile_id: autoFollowId }
          });
          sendRudderstackEvent(ANALYTICS_EVENTS.BREEDER_ADDED_TO_FAVORITES, {
            buyer_profile_id: buyerProfileId,
            breeder_profile_id: Number(autoFollowId),
            user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING
          });
        } else {
          await createUserFollow({
            body: {
              followable_type: autoFollowType,
              followable_id: autoFollowId
            }
          });
          const eventName =
            autoFollowType === FOLLOWABLE_TYPES.DOG
              ? ANALYTICS_EVENTS.PUPPY_ADDED_TO_FAVORITES
              : ANALYTICS_EVENTS.BREEDER_ADDED_TO_FAVORITES;
          sendRudderstackEvent(eventName, {
            buyer_profile_id: buyerProfileId,
            user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING,
            ...(autoFollowType === FOLLOWABLE_TYPES.DOG && {
              dog_id: Number(autoFollowId)
            })
          });
        }
      } finally {
        clearParams();
      }
    }

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, buyerProfileId, autoFollowType, autoFollowId]);

  useEffect(() => {
    if (!autoReactionableType || !autoReactionableId) return;
    if (!isLoggedIn || !buyerProfileId) return;

    function clearParams() {
      dispatch(resetParams(REACTIONABLE_TYPE, REACTIONABLE_ID, { hash: true }));
    }

    if (!KNOWN_REACTIONABLE_TYPES.includes(autoReactionableType)) {
      clearParams();
      return;
    }

    async function run() {
      try {
        await createUserReaction({
          body: {
            reactionable_type: autoReactionableType,
            reactionable_id: autoReactionableId,
            reaction_type: REACTION_TYPES.LIKE
          }
        });
        sendRudderstackEvent(ANALYTICS_EVENTS.SOCIAL_POST_INTERACTION_BY_USER, {
          interaction_type: ANALYTICS_INTERACTION_TYPES.LIKE,
          breeder_post_id: Number(autoReactionableId),
          buyer_profile_id: buyerProfileId,
          user_journey: ANALYTICS_USER_JOURNEYS.BUYER_BROWSING
        });
      } finally {
        clearParams();
      }
    }

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, buyerProfileId, autoReactionableType, autoReactionableId]);

  return null;
}
